import numeral from 'numeral';
import {
  type Channel, channel,
} from 'redux-saga';
import {
  call,
  put, take, takeEvery,
} from 'redux-saga/effects';
import type { DistanceMatrixResultItem } from '~/_shared/repositories/travelTime.repository';
import {
  DISTANCE_MATRIX_MAX_SEARCH_ITEMS_PER_REQUEST,
  DISTANCE_MATRIX_MAX_TO_LOCATIONS,
  getDistanceMatrixResults, prepareDistanceMatrixSearchRequest,
} from '~/_shared/repositories/useDistanceMatrixService';
import type { UnitSystem } from '~/_shared/types/googleMaps/googleMaps.types';
import { ProximityType } from '~/_shared/types/proximity/proximity.enums';
import { createUuid } from '~/_shared/utils/createUuid';
import { downloadFile } from '~/_shared/utils/document/document.helpers';
import { select } from '~/_shared/utils/saga/effects';
import { isNullOrUndefined } from '~/_shared/utils/typeGuards';
import type { PickAction } from '~/_shared/utils/types/action.type';
import { ExportDataFileType } from '~/data/exportDataModal/exportDataFileType.enum';
import {
  getItemizedSpreadsheetData, getItemizedSpreadsheetNextPage, type ItemizedSpreadsheetDataParams,
} from '~/data/exportDataModal/useExportItemizedSpreadsheetData';
import { mapSettingsSelector } from '~/map/map/useMapSettings.hook';
import { ModalType } from '~/modal/modalType.enum';
import { proximityListSelector } from '~/proximity/useProximities';
import {
  applyProximityResultsLimitsToRowsWithTravelData,
  createFrom1ToNSearchItemsFromProximitySpreadsheetData,
  getRequestsFromProximity, getRowsPerSpreadsheetResponseForInclusionType,
  proximityExportAddDistanceMatrixTravelDataToRows,
  proximityExportCalculateDistanceFromCenterForRows,
} from '~/proximityDetails/export/exportProximityData.helpers';
import type {
  PerProximitySpreadsheetRowsWithTravelData, ProximityExportFilter,
} from '~/proximityDetails/export/exportProximityData.types';
import { ProximityDistanceCalculateMethod } from '~/proximityDetails/export/useExportProximityDistanceCalculate';
import {
  ITEMIZED_EXPORT_PER_PAGE_ROWS_LIMIT, type ItemizedSpreadsheetDataResponse,
} from '~/spreadsheet/itemizeExport.repository';
import type { SpreadsheetDataBulkResponse } from '~/spreadsheet/spreadsheet.repository';
import { getSpreadsheetDataAsynchronously } from '~/spreadsheet/useGetBulkSpreadsheetDataAsynchronously';
import { appStore } from '~/store/app.store';
import { mapSettingsSettingsUnitSystemSelector } from '~/store/mapSettings/settings/mapSettingsSettings.selectors';
import {
  closeModal, openModalWithId,
} from '~/store/modal/modal.actionCreators';
import {
  selectFilteredLatLngSpreadsheetData, selectSpreadsheetData,
} from '~/store/selectors/spreadsheetDataMemoizedSelectors';
import { clientIdSelector } from '~/store/selectors/useClientIdSelector';
import { mapIdSelector } from '~/store/selectors/useMapIdSelector';
import { isMapPresentationalSelector } from '~/store/selectors/useMapInfoSelectors';
import { createAreaRequestGetterFilter } from '~/store/spreadsheetData/area/spreadsheetData.area.helpers';
import { getFilterTreeParamsFromMapSettings } from '~/store/spreadsheetData/filtering/useFilterTreeMapSettingsParams';
import type { LatLngRowData } from '~/store/spreadsheetData/spreadsheetData.helpers';
import { userSetDistanceMatrixUsage } from '~/store/userData/userData.actionCreators';
import { userDataSelector } from '~/store/userData/userData.selectors';
import type { UserDataState } from '~/store/userData/userData.state';
import { translate } from '~/translations/Trans';
import type { ProximityToolAction } from './proximityTool.action';
import {
  proximityExportCreate, proximityExportRemove, proximityExportSetError,
  proximityExportSetNextPagePending,
  proximityExportSetPageSuccessResults,
} from './proximityTool.actionCreators';
import { proximityToolExportsSelector } from './proximityTool.selectors';

export const CANCELLED_EXPORT = 'CANCELLED_EXPORT';

export function* proximityToolExportSagas() {
  yield takeEvery(proximityExportCreate, processProximityExportSaga);
  yield takeEvery(proximityExportSetNextPagePending, processProximityExportNextPageSaga);
}

function* processProximityExportSaga(action: PickAction<ProximityToolAction, typeof proximityExportCreate.type>) {
  const mapId: ReturnType<typeof mapIdSelector> = yield select(mapIdSelector);
  const clientId: ReturnType<typeof clientIdSelector> = yield select(clientIdSelector);
  const isMapPresentational: ReturnType<typeof isMapPresentationalSelector> = yield select(isMapPresentationalSelector);
  const mapSettingsData: ReturnType<typeof mapSettingsSelector> = yield select(mapSettingsSelector);
  const proximities: ReturnType<typeof proximityListSelector> = yield select(proximityListSelector);
  const latLngLookup: ReturnType<typeof selectFilteredLatLngSpreadsheetData> = yield select(selectFilteredLatLngSpreadsheetData);
  const { spreadsheetData }: ReturnType<typeof selectSpreadsheetData> = yield select(selectSpreadsheetData);
  const unitSystem: UnitSystem = yield select(mapSettingsSettingsUnitSystemSelector);
  const { filter: allFilteredSpreadsheetRows }: ReturnType<typeof selectFilteredLatLngSpreadsheetData> = yield select(selectFilteredLatLngSpreadsheetData);

  const filterTreeParams = getFilterTreeParamsFromMapSettings(mapSettingsData);
  const proximityExportId = action.payload.id;
  const {
    distanceCalculate,
    proximityIds,
    selectedSpreadsheetRowId,
    spreadsheetId,
    exportLimits,
    extension,
    exportAsText,
  } = action.payload.params;

  if (!clientId || !mapId
    || (distanceCalculate.calculateMethod === ProximityDistanceCalculateMethod.DrivingDistance
      && !!distanceCalculate.drivingDisabledReason
    )
  ) {
    yield put(proximityExportSetError(proximityExportId, translate('An unknown error has occurred.')));
    return;
  }

  const proximityList = proximities.filter(proximity => proximityIds.includes(proximity.id));

  const proximitiesFilters: ProximityExportFilter[] = [];

  for (const proximity of proximityList) {
    const proximityRequests = getRequestsFromProximity(proximity, latLngLookup.visible, spreadsheetData, selectedSpreadsheetRowId);

    if (proximityRequests.circles) {
      proximityRequests.circles.forEach(circle => {
        proximitiesFilters.push({
          type: ProximityType.DistanceRadius,
          proximity,
          circle,
        });
      });
    }

    if (proximityRequests.polygons && proximityRequests.polygons.length) {
      proximitiesFilters.push({
        type: ProximityType.DriveTimePolygon,
        proximity,
        polygons: proximityRequests.polygons,
      });
    }
  }

  const requestGetters = proximitiesFilters.map(proximityFilter => ({
    spreadsheet_id: spreadsheetId,
    map_id: mapId,
    exclude_row_data: true,
    exclude_basic_data: true,
    filter: createAreaRequestGetterFilter(
      true,
      'or',
      filterTreeParams,
      spreadsheetId,
      proximityFilter.type === ProximityType.DistanceRadius && proximityFilter.circle ? [proximityFilter.circle] : null,
      proximityFilter.type === ProximityType.DriveTimePolygon && proximityFilter.polygons ? proximityFilter.polygons : null,
      null,
      null,
    ),
  }));

  let bulkSpreadsheetData: SpreadsheetDataBulkResponse[] = [];

  try {
    bulkSpreadsheetData = yield call(getSpreadsheetDataAsynchronously, {
      clientId,
      mapId,
      isMapPresentational,
      request: { params: requestGetters },
    });
  }
  catch (error) {
    yield put(proximityExportSetError(proximityExportId, translate('An unknown error has occurred.')));
    return;
  }

  // after possibly lengthy bulk export we want to check if user did not cancel the export in the meantime
  const wasExportCancelled: boolean = yield call(wasProximityExportCancelled, proximityExportId);
  if (wasExportCancelled) {
    return;
  }

  const perProximityRows = getRowsPerSpreadsheetResponseForInclusionType(
    allFilteredSpreadsheetRows,
    spreadsheetId,
    bulkSpreadsheetData,
    exportLimits.markersInclusionType
  );
  let perProximityrowsWithTravelData: PerProximitySpreadsheetRowsWithTravelData = [];

  const shouldContinueIfExportIsBig: boolean = yield call(confirmIfBigExportWarning, {
    numberOfRows: perProximityRows.reduce((acc, rows) => acc + rows.length, 0),
    exportAsText,
  });

  if (!shouldContinueIfExportIsBig) {
    yield put(proximityExportRemove(proximityExportId));
  }

  if (distanceCalculate.calculateMethod === ProximityDistanceCalculateMethod.DrivingDistance
    && !distanceCalculate.drivingDisabledReason
  ) {
    try {
      perProximityrowsWithTravelData = yield call(getDriveTimeDistancesForProximityExport, {
        clientId,
        proximitiesFilters,
        perProximityRows,
        unitSystem,
      });
    }
    catch (error) {
      if (error instanceof Error && error.message === CANCELLED_EXPORT) {
        yield put(proximityExportRemove(proximityExportId));
        return;
      }
      yield put(proximityExportSetError(proximityExportId, translate('An unknown error has occurred.')));
      return;
    }
  }
  else {
    perProximityrowsWithTravelData = proximityExportCalculateDistanceFromCenterForRows(
      perProximityRows,
      proximitiesFilters,
      spreadsheetId,
      unitSystem
    );
  }

  // after getting the async travel data calls we want to check if user did not cancel the export in the meantime
  const wasExportCancelledAfterDistances: boolean = yield call(wasProximityExportCancelled, proximityExportId);
  if (wasExportCancelledAfterDistances) {
    return;
  }

  // limit the results according to the exportLimits
  perProximityrowsWithTravelData = applyProximityResultsLimitsToRowsWithTravelData(
    perProximityrowsWithTravelData,
    proximitiesFilters,
    exportLimits,
  );

  // remove proximities with no results
  perProximityrowsWithTravelData = perProximityrowsWithTravelData.filter(item => item.length > 0);

  // should we show travel time and distance columns? do they have values?
  const showTravelTime = perProximityrowsWithTravelData.some(rows => rows.some(row => !isNullOrUndefined(row.travelTimeColumnValue)));
  const showDistanceColumn = perProximityrowsWithTravelData.some(rows => rows.some(row => !isNullOrUndefined(row.distanceColumnValue)));

  // adding additional column values (proximity name and distance if enabled)
  const itemizedExportItems = perProximityrowsWithTravelData.map((rows, index) => {
    const proximity = proximitiesFilters[index];

    return {
      rows: rows
        .toSorted((a, b) => (a.distanceFromCenter || 0) - (b.distanceFromCenter || 0))
        .map(item => {
          const distanceColumnValue = Number(item.distanceColumnValue).toFixed(2);
          const travelTimeColumnValue = item.travelTimeColumnValue;

          return {
            rowId: item.rowId,
            postfixColumnsValues: [
              proximity?.proximity.name || '',
              ...(showDistanceColumn && !isNullOrUndefined(distanceColumnValue) ? ['' + distanceColumnValue] : []),
              ...(showTravelTime && !isNullOrUndefined(travelTimeColumnValue) ? [travelTimeColumnValue] : []),
            ],
          };
        }),
    };
  });

  const itemizedExportParams: ItemizedSpreadsheetDataParams = {
    additionalPostfixColumns: [
      { columnName: translate('Proximity Name') },
      ...(showDistanceColumn
        ? [{ columnName: translate('Distance To Center') + ' (' + translate(unitSystem) + ')' }]
        : []
      ),
      ...(showTravelTime ? [{ columnName: translate('Travel Time') }] : []),
    ],
    extension: exportAsText ? ExportDataFileType.Csv : extension,
    exportAsText: exportAsText ? true : undefined,
    items: itemizedExportItems,
  };

  let itemizedExportResponse: ItemizedSpreadsheetDataResponse | null = null;

  try {
    itemizedExportResponse = yield call(
      getItemizedSpreadsheetData,
      {
        clientId,
        mapId,
        isMapPresentational,
        params: itemizedExportParams,
      }
    );
  }
  catch (error) {
    yield put(proximityExportSetError(proximityExportId, translate('An unknown error has occurred.')));
    return;
  }

  if (itemizedExportResponse) {
    // after itemized export finish we want to check if user did not cancel the export in the meantime
    const wasExportCancelledAfterItemized: boolean = yield call(wasProximityExportCancelled, proximityExportId);
    if (wasExportCancelledAfterItemized) {
      return;
    }

    yield put(proximityExportSetPageSuccessResults({
      id: proximityExportId,
      pageIndex: 0,
      results: {
        nextPageId: itemizedExportResponse.next_hash,
        file: itemizedExportResponse?.data.file,
        data: itemizedExportResponse?.data.data,
      },
    }));

    if (itemizedExportResponse.data.file) {
      downloadFile(itemizedExportResponse.data.file);
    }
    if (itemizedExportResponse.next_hash) {
      yield put(proximityExportSetNextPagePending(proximityExportId));
    }
  }
  else {
    yield put(proximityExportSetError(proximityExportId, translate('An unknown error has occurred.')));
  }
}

function* processProximityExportNextPageSaga(action: PickAction<ProximityToolAction, typeof proximityExportSetNextPagePending.type>) {
  const proximityExportId = action.payload.id;

  const mapId: ReturnType<typeof mapIdSelector> = yield select(mapIdSelector);
  const clientId: ReturnType<typeof clientIdSelector> = yield select(clientIdSelector);
  const isMapPresentational: ReturnType<typeof isMapPresentationalSelector> = yield select(isMapPresentationalSelector);

  if (!clientId || !mapId) {
    return;
  }

  const proximityExportState: ReturnType<typeof proximityToolExportsSelector> = yield select(proximityToolExportsSelector);
  const proximityExportItem = proximityExportState.find(item => item.id === proximityExportId);

  if (!proximityExportItem) {
    return; // means it was cancelled
  }

  const penultimateIndex = proximityExportItem.resultPages.length - 2;
  if (penultimateIndex < 0) {
    console.warn('Proximity export next page called but there is no predecessor page');
    return;
  }

  const penultimatePage = proximityExportItem.resultPages[penultimateIndex];

  if (!penultimatePage || !penultimatePage.data?.nextPageId) {
    console.warn('Proximity export next page saga called but there is no penultimate page or next page id');
    return;
  }

  const itemizedExportNextPageResponse: ItemizedSpreadsheetDataResponse = yield call(getItemizedSpreadsheetNextPage, {
    clientId,
    mapId,
    isMapPresentational,
    nextPageId: penultimatePage.data.nextPageId,
  });

  yield put(proximityExportSetPageSuccessResults({
    id: proximityExportId,
    pageIndex: penultimateIndex + 1,
    results: {
      nextPageId: itemizedExportNextPageResponse.next_hash,
      file: itemizedExportNextPageResponse?.data.file,
    },
  }));

  if (itemizedExportNextPageResponse.data.file) {
    downloadFile(itemizedExportNextPageResponse.data.file);
  }
  if (itemizedExportNextPageResponse.next_hash) {
    yield put(proximityExportSetNextPagePending(proximityExportId));
  }
}

function* getDriveTimeDistancesForProximityExport({
  clientId,
  proximitiesFilters,
  perProximityRows,
  unitSystem,
}: {
  clientId: number;
  proximitiesFilters: ReadonlyArray<ProximityExportFilter>;
  perProximityRows: ReadonlyArray<ReadonlyArray<LatLngRowData>>;
  unitSystem: UnitSystem;
}) {
  const {
    searchItems,
    searchIdsPerProximityFilter,
  } = createFrom1ToNSearchItemsFromProximitySpreadsheetData(proximitiesFilters, perProximityRows);
  const distanceMatrixRequest = prepareDistanceMatrixSearchRequest(searchItems);

  const allowContinue: boolean = yield call(confirmUserAcceptsDriveTimeLimits, {
    isOverPerRequestLimit: !!distanceMatrixRequest.searchItemsOverLimit.length,
    numberOfSearches: distanceMatrixRequest.searchItemsRequest.searches.length,
  });

  if (!allowContinue) {
    throw new Error(CANCELLED_EXPORT);
  }

  const distanceMatrixResults: {
    results: ReadonlyArray<DistanceMatrixResultItem>;
    newDistanceMatrixRequestsUsage: number | undefined;
  } = yield call(
    getDistanceMatrixResults,
    {
      clientId,
      request: distanceMatrixRequest,
    },
  );

  if (distanceMatrixResults.newDistanceMatrixRequestsUsage) {
    yield put(userSetDistanceMatrixUsage(distanceMatrixResults.newDistanceMatrixRequestsUsage));
  }

  return proximityExportAddDistanceMatrixTravelDataToRows({
    proximityExportFilter: proximitiesFilters,
    distanceMatrixResults: distanceMatrixResults.results,
    searchIdsPerProximityFilter,
    proximitySpreadsheetData: perProximityRows,
    unitSystem,
    t: translate,
  });
}

function* confirmUserAcceptsDriveTimeLimits({
  isOverPerRequestLimit,
  numberOfSearches,
}: {
  isOverPerRequestLimit?: boolean;
  numberOfSearches: number;
}) {
  const userData: UserDataState = yield select(userDataSelector);
  const responseChannel: Channel<boolean> = yield call(channel);

  const potentialUsageNumber = Math.ceil(numberOfSearches / DISTANCE_MATRIX_MAX_SEARCH_ITEMS_PER_REQUEST);

  const onClose = () => {
    appStore.dispatch(closeModal(modalId));
  };

  const warningMessages: string[] = [
    ...(isOverPerRequestLimit ? [
      translate('proximityExport.overRequestLimitToLocations', {
        limit: numeral(DISTANCE_MATRIX_MAX_TO_LOCATIONS).format('0,0'),
      }),
    ] : []),
    // only if more than 10 requests we want to warn the user of usage
    ...(potentialUsageNumber > 10 && userData ? [
      translate('proximityExport.driveTimeUsage', {
        n: potentialUsageNumber,
        remaining: userData.limits.distanceMatrixRequests - userData.usage.distanceMatrixRequests,
      }),
    ] : []),
  ];

  if (warningMessages.length === 0) {
    return true;
  }

  const modalId = createUuid();

  yield put(openModalWithId(modalId, ModalType.Confirmation, {
    title: translate('Export Request Notice'),
    children: `${warningMessages.join('\n')}`,
    confirmCaption: translate('Proceed'),
    onCancel: () => {
      responseChannel.put(false);
      onClose();
    },
    onClose: () => {
      responseChannel.put(false);
      onClose();
    },
    onConfirm: () => {
      responseChannel.put(true);
      onClose();
    },
  }));

  const userConfirmed: boolean = yield take(responseChannel);
  responseChannel.close();
  return userConfirmed;
}

function* wasProximityExportCancelled(proximityExportId: string) {
  const proximityExportState: ReturnType<typeof proximityToolExportsSelector> = yield select(proximityToolExportsSelector);
  return !proximityExportState.find(item => item.id === proximityExportId);
}

function* confirmIfBigExportWarning({
  numberOfRows,
  exportAsText,
}: {
  numberOfRows: number;
  exportAsText?: boolean;
}) {
  const numberOfPages = Math.ceil(numberOfRows / ITEMIZED_EXPORT_PER_PAGE_ROWS_LIMIT);

  // if just one page and not many requests, no need to bother the user
  if (exportAsText || numberOfPages < 2) {
    return true;
  }

  const modalId = createUuid();
  const responseChannel: Channel<boolean> = yield call(channel);

  const onClose = () => {
    appStore.dispatch(closeModal(modalId));
  };

  yield put(openModalWithId(modalId, ModalType.Confirmation, {
    title: translate('Large Data Export Notice'),
    children: translate('The dataset you\'re exporting exceeds the size capacity for a single file and will need to be distributed across {{n}} files. Would you like to proceed?', {
      n: numberOfPages,
    }),
    confirmCaption: translate('Proceed'),
    onCancel: () => {
      responseChannel.put(false);
      onClose();
    },
    onClose: () => {
      responseChannel.put(false);
      onClose();
    },
    onConfirm: () => {
      responseChannel.put(true);
      onClose();
    },
  }));

  const userConfirmed: boolean = yield take(responseChannel);
  responseChannel.close();
  return userConfirmed;
}
